import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  ClipboardList, FolderOpen, Users, TrendingUp, CheckCircle2, FileEdit, Send, Eye,
  UserCog, UserCheck, Search, Filter, X, LayoutList, KanbanSquare, ExternalLink, LayoutGrid,
  ArrowUpRight, Check, User, Trash2, Sparkles
} from "lucide-react";
import { PageSkeleton, ScopeViewSkeleton } from "@/components/LoadingSkeleton";
import BoardView from "@/components/BoardView";
import RefreshControl from "@/components/RefreshControl";
import PublishingOverviewPanel from "@/components/PublishingOverviewPanel";
import CustomCell from "@/components/CustomCell";
import useSheetKit from "@/hooks/useSheetKit";
import { HeaderMenu, BulkUpdateColumnDialog } from "@/components/SheetHeaderKit";
import { ContextMenu, ContextMenuTrigger, ContextMenuContent, ContextMenuItem, ContextMenuSeparator } from "@/components/ui/context-menu";
import { Copy, ClipboardPaste } from "lucide-react";

const STATUSES = ["To-Do", "Editing Pending", "Publishing Pending", "Review Pending", "Published"];
const STATUS_META = {
  "To-Do":              { icon: ClipboardList, cls: "bg-slate-100 text-slate-700 border-slate-200",   active: "bg-slate-800 text-white border-slate-800",       bar: "bg-slate-400",   cell: "text-slate-700 hover:bg-slate-200",    activeCell: "bg-slate-800 text-white" },
  "Editing Pending":    { icon: FileEdit,      cls: "bg-amber-100 text-amber-700 border-amber-200",   active: "bg-amber-600 text-white border-amber-600",       bar: "bg-amber-500",   cell: "text-amber-800 hover:bg-amber-200",     activeCell: "bg-amber-600 text-white" },
  "Publishing Pending": { icon: Send,          cls: "bg-orange-100 text-orange-700 border-orange-200",active: "bg-orange-600 text-white border-orange-600",     bar: "bg-orange-500",  cell: "text-orange-800 hover:bg-orange-200",   activeCell: "bg-orange-600 text-white" },
  "Review Pending":     { icon: Eye,           cls: "bg-blue-100 text-blue-700 border-blue-200",      active: "bg-blue-700 text-white border-blue-700",         bar: "bg-blue-500",    cell: "text-blue-800 hover:bg-blue-200",       activeCell: "bg-blue-700 text-white" },
  "Published":          { icon: CheckCircle2,  cls: "bg-emerald-100 text-emerald-700 border-emerald-200", active: "bg-emerald-700 text-white border-emerald-700",bar: "bg-emerald-500", cell: "text-emerald-800 hover:bg-emerald-200", activeCell: "bg-emerald-700 text-white" },
};

const ROLE_META = {
  admin: { label: "Admin", cls: "bg-purple-100 text-purple-700 border-purple-200" },
  content_manager: { label: "Content Manager", cls: "bg-purple-100 text-purple-700 border-purple-200" },
  team_lead: { label: "Team Lead", cls: "bg-blue-100 text-blue-700 border-blue-200" },
  associate: { label: "Associate", cls: "bg-slate-100 text-slate-700 border-slate-200" },
};

const DEPT_LABELS = { content: "Content Team", seo: "SEO Team", marketing: "Marketing Team" };
const METHOD_OPTIONS = ["Human", "AI"];

export default function ScopeView() {
  const { kind, id } = useParams(); // kind: "manager" | "tl" | "department"
  const { user: currentUser } = useAuth();
  const [scopeUser, setScopeUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  // Iter 42.6 — server-aggregated scope stats. Replaces the old pattern of
  // iterating every task client-side to build KPIs / pipeline / per-project /
  // per-member counts, which froze the browser at 5k+ tasks. Shape:
  //   { total, by_status: {}, per_project: {pid: {total, by_status}}, by_user: {uid: count} }
  const [scopeStats, setScopeStats] = useState(null);
  // Custom + department-wide columns defined via /departments/:dept/columns.
  // Loaded per scope so TL/CM/Dept dashboards render the same columns admins
  // configured on the project spreadsheet.
  const [deptColumns, setDeptColumns] = useState([]);
  const [colPrefs, setColPrefs] = useState({ aliases: {}, hidden: [], order: [] });
  const [view, setView] = useState(() => localStorage.getItem("scopeView") || "visual");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ status: "", writer_id: "", publisher_id: "", project_id: "" });
  const [selected, setSelected] = useState(new Set());
  const [lastClickedIdx, setLastClickedIdx] = useState(null);
  const dragRef = useRef({ dragging: false, startIdx: null, initial: null });
  // Guards against stale reload responses overwriting a newer scope. Every reload
  // captures the scope it was issued for; if the URL scope changed while its
  // fetches were in flight, we drop its updates instead of clobbering fresh state.
  const activeScopeRef = useRef(`${kind}:${id}`);
  useEffect(() => { activeScopeRef.current = `${kind}:${id}`; }, [kind, id]);

  const canBulk = (currentUser?.role === "admin" || currentUser?.role === "super_admin") || currentUser?.role === "content_manager" || currentUser?.role === "team_lead";

  useEffect(() => { localStorage.setItem("scopeView", view); }, [view]);
  // Clear selection when switching between list ↔ visual so users aren't surprised on return
  useEffect(() => { setSelected(new Set()); setLastClickedIdx(null); }, [view]);

  const reload = React.useCallback(async () => {
    const requestScope = `${kind}:${id}`;
    // Iter 42.8 — CRITICAL crash fix. Visual dashboards used to download up
    // to 5000 tasks and shove them all into BoardView, which spawned ~75k
    // DOM nodes and OOM-crashed the tab on departments with thousands of
    // tasks ("Aw, Snap! Error code: 5"). Now:
    //   • VISUAL view fetches only the 300 most-recently-updated tasks
    //     (~15k DOM nodes → smooth). KPIs still show the FULL total via the
    //     server-aggregated /dashboard/scope-stats endpoint.
    //   • LIST view still gets the full slice (up to 5000). It's virtualized
    //     downstream so DOM size stays flat regardless of task count.
    const isVisual = view === "visual";
    const taskLimit = isVisual ? 300 : 5000;
    const taskSort = isVisual ? "&sort=updated_at:desc" : "";
    let taskUrl = `/tasks?limit=${taskLimit}${taskSort}`;
    if (kind === "manager") taskUrl = `/tasks?content_manager_id=${id}&limit=${taskLimit}${taskSort}`;
    else if (kind === "tl") taskUrl = `/tasks?team_lead_id=${id}&limit=${taskLimit}${taskSort}`;
    else if (kind === "department") taskUrl = `/tasks?department=${id}&limit=${taskLimit}${taskSort}`;

    // Iter 42.6 — pull KPI/pipeline/per-project/per-user counts via a
    // server-side aggregation (`/dashboard/scope-stats`) so the visual
    // dashboard renders in <200ms even at 100k tasks. The task list itself
    // is still fetched (bounded by the backend's 5000-row cap) so the
    // kanban BoardView + list view keep working — but STATS never depend
    // on it any more, so the browser doesn't freeze on the KPI cards.
    const statsUrl = `/dashboard/scope-stats${(kind && id) ? `?kind=${kind}&id=${id}` : ""}`;

    const [uAll, t, p, shares, stats] = await Promise.all([
      api.get("/users"),
      api.get(taskUrl),
      api.get("/projects"),
      api.get("/shares/me").catch(() => ({ data: [] })),
      api.get(statsUrl).catch(() => ({ data: null })),
    ]);
    // If the URL scope changed while these requests were in flight, discard
    // the response — otherwise we'd render another scope's data. This is the
    // root cause of the "TL/CM dashboard silently swaps to Priya's after 15-20s"
    // bug when the auto-refresh timer or a slow response fires after nav.
    if (activeScopeRef.current !== requestScope) return;
    setUsers(uAll.data);
    setTasks(t.data);
    setScopeStats(stats.data);

    const findUser = (uid) => uAll.data.find((x) => x.id === uid);

    let scopeDept = "content";
    if (kind === "manager") {
      const found = findUser(id);
      if (found) setScopeUser(found);
      else {
        const share = (shares.data || []).find((s) => s.scope_kind === "manager" && s.scope_id === id);
        setScopeUser({ id, name: share?.scope_name || "Content Manager", email: null });
      }
      setProjects(p.data.filter((pr) => (pr.content_manager_ids || []).includes(id)));
      scopeDept = (found?.departments?.[0]) || found?.department || "content";
    } else if (kind === "tl") {
      const found = findUser(id);
      if (found) {
        // If this TL belongs to a team (has team_name + manager_id), gather
        // the team-mate TL(s) so we can render both emails / designations in
        // the header — the dashboard is shared across Content TL + Publishing
        // TL, so the header should surface both contacts.
        const teamMates = (uAll.data || []).filter((u) =>
          u.role === "team_lead" &&
          u.id !== found.id &&
          u.team_name &&
          found.team_name &&
          u.team_name === found.team_name &&
          u.manager_id === found.manager_id
        );
        setScopeUser({ ...found, team_mates: teamMates });
      }
      else {
        const share = (shares.data || []).find((s) => s.scope_kind === "tl" && s.scope_id === id);
        setScopeUser({ id, name: share?.scope_name || "Team Lead", email: null, team_mates: [] });
      }
      setProjects(p.data.filter((pr) => (pr.team_lead_ids || []).includes(id)));
      scopeDept = (found?.departments?.[0]) || found?.department || "content";
    } else {
      setScopeUser({ name: DEPT_LABELS[id] || id, email: null, department: id });
      setProjects(p.data.filter((pr) => (pr.department || "content") === id));
      scopeDept = id || "content";
    }

    // Fetch department-configured columns + saved column prefs. Best-effort —
    // failures shouldn't block the primary scope render, but we still want the
    // race guard to hold before writing.
    try {
      const [colsRes, prefsRes] = await Promise.all([
        api.get(`/departments/${scopeDept}/columns`).catch(() => ({ data: [] })),
        api.get(`/departments/${scopeDept}/column-prefs`).catch(() => ({ data: {} })),
      ]);
      if (activeScopeRef.current !== requestScope) return;
      setDeptColumns(colsRes.data || []);
      setColPrefs({
        aliases: prefsRes.data?.aliases || {},
        hidden: prefsRes.data?.hidden || [],
        order: prefsRes.data?.order || [],
      });
    } catch (e) { console.debug("scope-view non-blocking:", e&&e.message); }
  }, [kind, id, view]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Iter 42.8 — Only reset filters/selection when the SCOPE actually changes
  // (kind/id), NOT on every view toggle. This lets a user set filters in
  // visual mode, jump to list view, and keep the same filters applied.
  //
  // Iter 45.19 — Also clear the previous scope's payload (scopeUser, tasks,
  // projects, scopeStats, deptColumns) so the `if (!scopeUser) return
  // <PageSkeleton>` guard fires IMMEDIATELY on switch. Without this, the
  // dashboard kept showing the previous person's rows/stats for the ~200-800ms
  // fetch window when jumping between CM/TL nodes in the sidebar (user report:
  // "when we switch from one hirarchy to other hirarchy person, the dashboard
  // gets delayed for some seconds and still shows the previous hirarchy
  // dashboard"). Clearing here trades a brief skeleton flash for a clean,
  // unambiguous transition — no stale data leak.
  useEffect(() => {
    setFilters({ status: "", writer_id: "", publisher_id: "", project_id: "" });
    setSearch("");
    setSelected(new Set());
    setLastClickedIdx(null);
    setScopeUser(null);
    setTasks([]);
    setProjects([]);
    setScopeStats(null);
    setDeptColumns([]);
  }, [kind, id]);

  // End drag anywhere on the page
  useEffect(() => {
    const onUp = () => { dragRef.current.dragging = false; };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, []);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const projectMap = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const assignables = users.filter((u) => u.role === "associate" || u.role === "team_lead");

  const updateTask = async (taskId, patch) => {
    const task = tasks.find((t) => t.id === taskId);
    // Compute inverse for undo
    const inverse = {};
    if (task) {
      for (const k of Object.keys(patch)) inverse[k] = task[k] ?? null;
    }
    // optimistic update
    setTasks((prev) => prev.map((t) => (t.id === taskId ? { ...t, ...patch, updated_at: new Date().toISOString() } : t)));
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, patch);
      setTasks((prev) => prev.map((t) => (t.id === taskId ? data : t)));
      if (task && sheet?.pushUndo) {
        sheet.pushUndo({
          label: `edit ${Object.keys(patch).join(", ")}`,
          revert: async () => {
            const { data: r } = await api.patch(`/tasks/${taskId}`, inverse);
            setTasks((prev) => prev.map((t) => (t.id === taskId ? r : t)));
          },
          redo: async () => {
            const { data: r } = await api.patch(`/tasks/${taskId}`, patch);
            setTasks((prev) => prev.map((t) => (t.id === taskId ? r : t)));
          },
        });
      }
    } catch (e) {
      // rollback on error by reloading
      try {
        let taskUrl = "/tasks";
        if (kind === "manager") taskUrl = `/tasks?content_manager_id=${id}`;
        else if (kind === "tl") taskUrl = `/tasks?team_lead_id=${id}`;
        else if (kind === "department") taskUrl = `/tasks?department=${id}`;
        const { data } = await api.get(taskUrl);
        setTasks(data);
      } catch (e) { console.debug("scope-view catch:", e&&e.message); }
      toast.error(formatApiError(e));
    }
  };

  const bulkPatch = async (patch) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const snapshotById = Object.fromEntries(
      tasks.filter((t) => ids.includes(t.id)).map((t) => {
        const inv = {}; for (const k of Object.keys(patch)) inv[k] = t[k] ?? null;
        return [t.id, inv];
      })
    );
    // Optimistic
    setTasks((prev) => prev.map((t) => ids.includes(t.id) ? { ...t, ...patch } : t));
    try {
      const { data } = await api.post("/tasks/bulk-update", { task_ids: ids, patch });
      const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId[t.id] || t));
      toast.success(`Updated ${data.updated} task${data.updated === 1 ? "" : "s"}`);
      if (sheet?.pushUndo) {
        const invList = Object.entries(snapshotById).map(([task_id, p]) => ({ task_id, patch: p }));
        sheet.pushUndo({
          label: `bulk edit ${Object.keys(patch).join(", ")}`,
          revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: invList }); } catch (e) { console.debug("scope-view catch:", e&&e.message); }; reload(); },
          redo:   async () => { try { await api.post("/tasks/bulk-update", { task_ids: ids, patch }); } catch (e) { console.debug("scope-view catch:", e&&e.message); }; reload(); },
        });
      }
    } catch (e) { toast.error(formatApiError(e)); reload(); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`Delete ${selected.size} task${selected.size === 1 ? "" : "s"}?`)) return;
    const ids = Array.from(selected);
    const snapshots = tasks.filter((t) => ids.includes(t.id));
    let liveIds = [...ids];
    // Optimistic
    setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id)));
    setSelected(new Set());
    try {
      const { data } = await api.post("/tasks/bulk-delete", { task_ids: liveIds });
      toast.success(`Deleted ${data.deleted} task${data.deleted === 1 ? "" : "s"}`);
      if (sheet?.pushUndo) {
        sheet.pushUndo({
          label: `delete ${snapshots.length} tasks`,
          revert: async () => {
            const results = await Promise.all(snapshots.map((s) => {
              const { id: _oid, created_at, updated_at, order, ...rest } = s;
              return api.post("/tasks", { ...rest, project_id: s.project_id, position: "bottom" }).then((r) => r.data).catch(() => null);
            }));
            const created = results.filter(Boolean);
            const newIds = created.map((t) => t.id);
            if (newIds.length) liveIds = newIds;
            setTasks((prev) => [...prev, ...created]);
          },
          redo: async () => {
            try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch (e) { console.debug("scope-view catch:", e&&e.message); }
            setTasks((prev) => prev.filter((t) => !liveIds.includes(t.id)));
          },
        });
      }
    } catch (e) {
      setTasks((prev) => [...snapshots, ...prev]);
      toast.error(formatApiError(e));
    }
  };

  const [bulkUpdateCol, setBulkUpdateCol] = useState(null);
  const applyBulkUpdate = async (col, newValue, limit) => {
    const targets = filtered.slice(0, limit);
    if (targets.length === 0) return;
    const patch = col.setValInto(newValue, targets[0]);
    if ("custom" in patch) {
      // Custom-col path — bulk-patch-many with per-task custom merges
      const updates = targets.map((t) => ({ task_id: t.id, patch: col.setValInto(newValue, t) }));
      const inverses = targets.map((t) => ({ task_id: t.id, patch: col.setValInto(col.getVal(t), t) }));
      setTasks((prev) => { const byId = Object.fromEntries(updates.map((u) => [u.task_id, u.patch])); return prev.map((t) => byId[t.id] ? { ...t, ...byId[t.id] } : t); });
      try {
        const { data } = await api.post("/tasks/bulk-patch-many", { updates });
        const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
        setTasks((prev) => prev.map((t) => byId[t.id] || t));
        toast.success(`Updated ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
        sheet.pushUndo({
          label: `bulk update ${col.label}`,
          revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: inverses }); } catch (e) { console.debug("scope-view catch:", e&&e.message); } reload(); },
          redo:   async () => { try { await api.post("/tasks/bulk-patch-many", { updates }); } catch (e) { console.debug("scope-view catch:", e&&e.message); } reload(); },
        });
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    const inverseById = Object.fromEntries(targets.map((t) => [t.id, col.setValInto(col.getVal(t), t)]));
    const ids = targets.map((t) => t.id);
    setTasks((prev) => prev.map((t) => (inverseById[t.id] ? { ...t, ...patch } : t)));
    try {
      const { data } = await api.post("/tasks/bulk-update", { task_ids: ids, patch });
      const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId[t.id] || t));
      toast.success(`Updated ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
      const invList = Object.entries(inverseById).map(([task_id, p]) => ({ task_id, patch: p }));
      sheet.pushUndo({
        label: `bulk update ${col.label}`,
        revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: invList }); } catch (e) { console.debug("scope-view catch:", e&&e.message); } reload(); },
        redo:   async () => { try { await api.post("/tasks/bulk-update", { task_ids: ids, patch }); } catch (e) { console.debug("scope-view catch:", e&&e.message); } reload(); },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const emptyValueForColScope = (col) => {
    switch (col.type) {
      case "bool": return false;
      case "user":
      case "member": return null;
      default: return "";
    }
  };
  const onScopeColClear = async (col) => {
    if (!col.editable) return;
    if (!confirm(`Clear all ${filtered.length} values in "${col.label}"?`)) return;
    const emptyVal = emptyValueForColScope(col);
    const targets = filtered.filter((t) => col.getVal(t) !== emptyVal);
    if (targets.length === 0) { toast.message("Column is already empty"); return; }
    const patch = col.setValInto(emptyVal, targets[0]);
    const ids = targets.map((t) => t.id);
    const inverseById = Object.fromEntries(targets.map((t) => [t.id, col.setValInto(col.getVal(t), t)]));
    setTasks((prev) => prev.map((t) => (inverseById[t.id] ? { ...t, ...patch } : t)));
    try {
      const { data } = await api.post("/tasks/bulk-update", { task_ids: ids, patch });
      const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
      setTasks((prev) => prev.map((t) => byId[t.id] || t));
      toast.success(`Cleared ${data.updated} cell${data.updated === 1 ? "" : "s"}`);
      const invList = Object.entries(inverseById).map(([task_id, p]) => ({ task_id, patch: p }));
      sheet.pushUndo({
        label: `clear ${col.label}`,
        revert: async () => { try { await api.post("/tasks/bulk-patch-many", { updates: invList }); } catch (e) { console.debug("scope-view catch:", e&&e.message); } reload(); },
        redo:   async () => { try { await api.post("/tasks/bulk-update", { task_ids: ids, patch }); } catch (e) { console.debug("scope-view catch:", e&&e.message); } reload(); },
      });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const renderScopeHeader = (colIdx, thClassName) => {
    const cd = scopeColumnDefs[colIdx];
    return (
      <th className={thClassName}>
        <div className="flex items-center gap-1">
          <span className="flex-1 truncate">{cd.label}</span>
          {canBulk && cd.editable && (
            <HeaderMenu
              col={cd} isAdmin={false} canBulk={canBulk}
              onBulkUpdate={() => setBulkUpdateCol(cd)}
              onClearColumn={() => onScopeColClear(cd)}
            />
          )}
        </div>
      </th>
    );
  };

  const filtered = useMemo(() => tasks.filter((t) => {
    if (search && !(`${t.keyword} ${t.niche} ${t.notes}`.toLowerCase().includes(search.toLowerCase()))) return false;
    if (filters.status && t.status !== filters.status) return false;
    if (filters.writer_id && t.writer_id !== filters.writer_id) return false;
    if (filters.publisher_id && t.publisher_id !== filters.publisher_id) return false;
    if (filters.project_id && t.project_id !== filters.project_id) return false;
    return true;
  }), [tasks, search, filters]);

  // Iter 45.3 — CRITICAL crash fix. Scope list view previously rendered ALL
  // filtered tasks into the DOM (`filtered.map`). With 2000+ tasks × 12+
  // columns × heavy per-cell wrappers (ContextMenu, sheet-kit cellProps,
  // AutofillHandle, CustomCell) this pushed the DOM past 25k nodes and
  // hung the browser for 60+ seconds when a Content Manager or Team Lead
  // was clicked in the sidebar. We now virtualize the tbody with
  // @tanstack/react-virtual — same pattern already proven on ProjectView
  // — so only the ~30 rows currently in the viewport exist in the DOM.
  const scrollContainerRef = useRef(null);
  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => 40,
    // Iter 45.22 — deeper overscan buffer for iOS/Android-style momentum
    // flick scrolling (same reasoning as ProjectView's virtualizer).
    overscan: 30,
    getItemKey: (index) => filtered[index]?.id ?? index,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();
  const virtualTotalSize = rowVirtualizer.getTotalSize();
  const virtualPaddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
  const virtualPaddingBottom = virtualItems.length > 0
    ? virtualTotalSize - virtualItems[virtualItems.length - 1].end
    : 0;

  // Editing state for sheet-kit-driven inline editors (keyword/niche)
  const [editingCoord, setEditingCoord] = useState(null); // { taskId, field }

  // Sheet-kit columns — Scope list-view schema. Column indices 0-8 are the
  // "legacy" fixed cells that keep their bespoke inline editors (rendered
  // explicitly in the table body). Indices >=9 are additional base columns
  // (ai_score, draft, check, notes) plus every department-configured custom
  // column, rendered generically via CustomCell/base fallback so admins see the
  // same columns they built on the project spreadsheet.
  const scopeColumnDefs = useMemo(() => {
    const findUserByName = (name) => assignables.find((u) => u.name.toLowerCase() === (name || "").trim().toLowerCase());
    const parseSelectOpts = (options) => (text) => {
      const s = (text ?? "").trim();
      if (s === "") return "";
      const found = options.find((o) => o.toLowerCase() === s.toLowerCase());
      return found ?? undefined;
    };
    const base = [
      { key: "date", label: "Date", type: "date", editable: canBulk,
        getVal: (t) => t.date || "", setValInto: (v) => ({ date: v }),
        textOf: (t) => t.date || "", parseText: (text) => (text ?? "").trim() },
      { key: "keyword", label: "Keyword", type: "text", editable: canBulk,
        getVal: (t) => t.keyword || "", setValInto: (v) => ({ keyword: v }),
        textOf: (t) => t.keyword || "", parseText: (text) => text ?? "" },
      { key: "project_id", label: "Project", type: "text", editable: false,
        getVal: (t) => t.project_id || "", setValInto: (v) => ({ project_id: v }),
        textOf: (t, ctx) => ctx?.projectMap?.[t.project_id]?.name || "" },
      { key: "niche", label: "Niche", type: "text", editable: canBulk,
        getVal: (t) => t.niche || "", setValInto: (v) => ({ niche: v }),
        textOf: (t) => t.niche || "", parseText: (text) => text ?? "" },
      { key: "method", label: "Method", type: "select", options: METHOD_OPTIONS, editable: canBulk,
        getVal: (t) => t.method || "", setValInto: (v) => ({ method: v }),
        textOf: (t) => t.method || "", parseText: parseSelectOpts(METHOD_OPTIONS) },
      { key: "writer_id", label: "Writer", type: "user", editable: canBulk, userList: assignables,
        getVal: (t) => t.writer_id || null,
        setValInto: (v) => ({ writer_id: v || null }),
        textOf: (t, ctx) => ctx?.userMap?.[t.writer_id]?.name || "",
        parseText: (text) => {
          const s = (text || "").trim();
          if (s === "" || s.toLowerCase() === "unassigned") return null;
          return findUserByName(s)?.id ?? undefined;
        } },
      { key: "publisher_id", label: "Publisher", type: "user", editable: canBulk, userList: assignables,
        getVal: (t) => t.publisher_id || null,
        setValInto: (v) => ({ publisher_id: v || null }),
        textOf: (t, ctx) => ctx?.userMap?.[t.publisher_id]?.name || "",
        parseText: (text) => {
          const s = (text || "").trim();
          if (s === "" || s.toLowerCase() === "unassigned") return null;
          return findUserByName(s)?.id ?? undefined;
        } },
      { key: "status", label: "Status", type: "select", options: STATUSES, editable: canBulk,
        getVal: (t) => t.status || "", setValInto: (v) => ({ status: v }),
        textOf: (t) => t.status || "", parseText: parseSelectOpts(STATUSES) },
      { key: "docs", label: "Docs", type: "url", editable: canBulk,
        getVal: (t) => t.docs || "", setValInto: (v) => ({ docs: v }),
        textOf: (t) => t.docs || "", parseText: (text) => text ?? "" },
      // Extra base columns (index >=9) rendered generically after the legacy
      // cells so the ScopeView list matches the ProjectView spreadsheet.
      { key: "ai_score", label: "AI Score", type: "text", editable: canBulk,
        getVal: (t) => t.ai_score || "", setValInto: (v) => ({ ai_score: v }),
        textOf: (t) => (t.ai_score ?? "").toString(), parseText: (text) => text ?? "" },
      { key: "draft", label: "Draft", type: "url", editable: canBulk,
        getVal: (t) => t.draft || "", setValInto: (v) => ({ draft: v }),
        textOf: (t) => t.draft || "", parseText: (text) => text ?? "" },
      { key: "check", label: "Check", type: "bool", editable: canBulk,
        getVal: (t) => !!t.check, setValInto: (v) => ({ check: !!v }),
        textOf: (t) => t.check ? "TRUE" : "",
        parseText: (text) => {
          const s = (text || "").trim().toLowerCase();
          if (["true","1","yes","x","✓"].includes(s)) return true;
          if (["false","0","no",""].includes(s)) return false;
          return undefined;
        } },
      { key: "notes", label: "Notes", type: "text", editable: canBulk,
        getVal: (t) => t.notes || "", setValInto: (v) => ({ notes: v }),
        textOf: (t) => t.notes || "", parseText: (text) => text ?? "" },
    ];
    const custom = (deptColumns || []).map((c) => ({
      key: `custom.${c.id}`, colId: c.id, label: c.label, type: c.type,
      options: c.options, isCustom: true, editable: canBulk,
      userList: c.type === "member" ? assignables : undefined,
      getVal: (t) => t.custom?.[c.id] ?? "",
      setValInto: (v, task) => ({ custom: { ...(task.custom || {}), [c.id]: v } }),
      textOf: (t, ctx) => {
        const v = t.custom?.[c.id];
        if (v == null || v === "") return "";
        if (c.type === "member") return ctx?.userMap?.[v]?.name || "";
        return String(v);
      },
      parseText: (text) => {
        const s = (text || "").trim();
        if (c.type === "dropdown") {
          if (s === "") return "";
          const found = (c.options || []).find((o) => o.toLowerCase() === s.toLowerCase());
          return found ?? undefined;
        }
        if (c.type === "member") {
          if (s === "" || s.toLowerCase() === "unassigned") return "";
          return findUserByName(s)?.id ?? undefined;
        }
        if (c.type === "number") return s === "" ? "" : (isNaN(Number(s)) ? undefined : Number(s));
        return s;
      },
    }));
    return [...base, ...custom];
  }, [assignables, canBulk, deptColumns]);

  // Apply per-department prefs (alias / hidden / order) so admin renames &
  // hidden columns from the project spreadsheet propagate to scope dashboards.
  // Legacy cells (index 0-8) are always kept in their fixed order for their
  // bespoke editors; only extra + custom columns (index >=9) respect ordering.
  const LEGACY_COUNT = 9;
  const visibleColumnDefs = useMemo(() => {
    const aliased = scopeColumnDefs.map((c) => ({ ...c, label: colPrefs.aliases?.[c.key] ?? c.label }));
    const legacy = aliased.slice(0, LEGACY_COUNT).filter((c) => !colPrefs.hidden?.includes(c.key));
    const extras = aliased.slice(LEGACY_COUNT).filter((c) => !colPrefs.hidden?.includes(c.key));
    if (!colPrefs.order || colPrefs.order.length === 0) return [...legacy, ...extras];
    const keyPos = Object.fromEntries(colPrefs.order.map((k, i) => [k, i]));
    const sortedExtras = [...extras].sort((a, b) => (keyPos[a.key] ?? 9999) - (keyPos[b.key] ?? 9999));
    return [...legacy, ...sortedExtras];
  }, [scopeColumnDefs, colPrefs]);

  // Extra columns rendered generically after the legacy cells (indices > 8 in
  // visibleColumnDefs). Used by the <thead>/<tbody> below to iterate.
  const extraVisibleColumnDefs = useMemo(() => {
    const legacyKeys = new Set(scopeColumnDefs.slice(0, LEGACY_COUNT).map((c) => c.key));
    return visibleColumnDefs.filter((c) => !legacyKeys.has(c.key));
  }, [visibleColumnDefs, scopeColumnDefs]);

  // Department the scope belongs to — used to scope the embedded Publishing
  // Overview panel and to pass to CustomCell fallback for members list.
  const scopeDept = useMemo(() => {
    if (kind === "department") return id;
    return (scopeUser?.departments?.[0]) || scopeUser?.department || "content";
  }, [kind, id, scopeUser]);

  const sheet = useSheetKit({
    rows: filtered,
    columnDefs: scopeColumnDefs,
    canEdit: canBulk,
    context: { userMap, projectMap },
    onPatchTask: async (task, patch) => {
      // Optimistic
      setTasks((prev) => prev.map((t) => (t.id === task.id ? { ...t, ...patch } : t)));
      try {
        const { data } = await api.patch(`/tasks/${task.id}`, patch);
        setTasks((prev) => prev.map((t) => (t.id === task.id ? data : t)));
        return data;
      } catch (e) {
        toast.error(formatApiError(e));
        setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
        throw e;
      }
    },
    onBulkPatch: async (updates) => {
      if (!updates || updates.length === 0) return [];
      const patchById = Object.fromEntries(updates.map((u) => [u.task_id, u.patch]));
      const beforeSnap = tasks.filter((t) => patchById[t.id]).map((t) => ({ ...t }));
      setTasks((prev) => prev.map((t) => (patchById[t.id] ? { ...t, ...patchById[t.id] } : t)));
      try {
        const { data } = await api.post("/tasks/bulk-patch-many", { updates });
        const byId = Object.fromEntries((data.tasks || []).map((t) => [t.id, t]));
        setTasks((prev) => prev.map((t) => byId[t.id] || t));
        return data.tasks || [];
      } catch (e) {
        setTasks((prev) => { const bi = Object.fromEntries(beforeSnap.map((t) => [t.id, t])); return prev.map((t) => bi[t.id] || t); });
        toast.error(formatApiError(e));
        throw e;
      }
    },
    onCreateTask: null,
    onDeleteTasks: async (taskIds) => {
      await api.post("/tasks/bulk-delete", { task_ids: taskIds });
      setTasks((prev) => prev.filter((t) => !taskIds.includes(t.id)));
      setSelected((prev) => { const n = new Set(prev); taskIds.forEach((tid) => n.delete(tid)); return n; });
    },
  });

  // Bridge: sheet.editing → editingCoord (for keyword/niche inline text editors)
  useEffect(() => {
    if (!sheet.editing) { setEditingCoord(null); return; }
    const col = scopeColumnDefs[sheet.editing.c];
    const task = filtered[sheet.editing.r];
    if (col && task && (col.key === "keyword" || col.key === "niche")) {
      setEditingCoord({ taskId: task.id, field: col.key });
    } else setEditingCoord(null);
    // eslint-disable-next-line
  }, [sheet.editing]);

  // Autofill drag
  useEffect(() => {
    if (!sheet.autofill) return;
    const onMove = (e) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const td = el && el.closest ? el.closest("td[data-cell]") : null;
      if (!td) return;
      const parts = (td.dataset.cell || "").split("-");
      const r = Number(parts[0]);
      if (!Number.isNaN(r)) sheet.extendAutofill(r);
    };
    const onUp = async () => { await sheet.commitAutofill(); };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp, { once: true });
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line
  }, [sheet.autofill]);

  // Delete-key: hybrid — row-checkbox delete rows; else sheet-kit clears cells
  useEffect(() => {
    if (!canBulk) return;
    const onKey = (e) => {
      if (!(e.key === "Delete" || e.key === "Backspace")) return;
      if (selected.size === 0) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      bulkDelete();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line
  }, [canBulk, selected]);

  const filteredIds = useMemo(() => filtered.map((t) => t.id), [filtered]);
  const allSelected = filteredIds.length > 0 && filteredIds.every((tid) => selected.has(tid));
  const someSelected = filteredIds.some((tid) => selected.has(tid));

  const toggleAll = () => {
    if (allSelected) {
      const next = new Set(selected);
      filteredIds.forEach((tid) => next.delete(tid));
      setSelected(next);
    } else setSelected(new Set([...selected, ...filteredIds]));
  };
  const toggleOne = (tid) => {
    const n = new Set(selected);
    if (n.has(tid)) n.delete(tid); else n.add(tid);
    setSelected(n);
  };
  const startRowSelect = (idx, e) => {
    if (!canBulk) return;
    e.preventDefault();
    const additive = e.metaKey || e.ctrlKey;
    const rangeExtend = e.shiftKey && lastClickedIdx !== null;
    if (rangeExtend) {
      const from = Math.min(lastClickedIdx, idx);
      const to = Math.max(lastClickedIdx, idx);
      const next = new Set(selected);
      for (let i = from; i <= to; i++) if (filtered[i]) next.add(filtered[i].id);
      setSelected(next); return;
    }
    const base = additive ? new Set(selected) : new Set();
    const tid = filtered[idx]?.id;
    if (tid) { if (additive && selected.has(tid)) base.delete(tid); else base.add(tid); }
    setSelected(base); setLastClickedIdx(idx);
    dragRef.current = { dragging: true, startIdx: idx, initial: additive ? new Set(selected) : new Set() };
  };
  const dragOverRow = (idx) => {
    if (!dragRef.current.dragging) return;
    const from = Math.min(dragRef.current.startIdx, idx);
    const to = Math.max(dragRef.current.startIdx, idx);
    const next = new Set(dragRef.current.initial);
    for (let i = from; i <= to; i++) if (filtered[i]) next.add(filtered[i].id);
    setSelected(next);
  };

  const stats = useMemo(() => {
    // Iter 42.6 — prefer server-aggregated stats (constant payload / O(1) in
    // task count) over the client-side pass. Falls back to local iteration
    // only when the endpoint is unreachable or hasn't responded yet.
    if (scopeStats && typeof scopeStats.total === "number") {
      const by_status = Object.fromEntries(STATUSES.map((s) => [s, scopeStats.by_status?.[s] || 0]));
      const per_project = {};
      for (const [pid, agg] of Object.entries(scopeStats.per_project || {})) {
        per_project[pid] = { total: agg.total || 0, ...Object.fromEntries(STATUSES.map((s) => [s, agg.by_status?.[s] || 0])) };
      }
      return { by_status, per_project, total: scopeStats.total };
    }
    const by_status = Object.fromEntries(STATUSES.map((s) => [s, 0]));
    const per_project = {};
    tasks.forEach((t) => {
      by_status[t.status] = (by_status[t.status] || 0) + 1;
      if (!per_project[t.project_id]) {
        per_project[t.project_id] = { total: 0, ...Object.fromEntries(STATUSES.map((s) => [s, 0])) };
      }
      per_project[t.project_id].total += 1;
      per_project[t.project_id][t.status] = (per_project[t.project_id][t.status] || 0) + 1;
    });
    return { by_status, per_project, total: tasks.length };
  }, [tasks, scopeStats]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  // Click a status pill or a project×status cell → jump to list view with filters applied.
  // Clicking the same status/cell again clears the filter.
  const showListForStatus = (status, project_id = "") => {
    setView("list");
    setFilters((f) => {
      const sameStatus = f.status === status;
      const sameProject = (f.project_id || "") === (project_id || "");
      if (sameStatus && sameProject) {
        // toggle off — clear these filters
        return { ...f, status: "", project_id: "" };
      }
      return { ...f, status, project_id };
    });
    setTimeout(() => {
      const el = document.querySelector('[data-testid="scope-task-list"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // Iter 45.20 — Use a full-width ScopeViewSkeleton (mirrors this page's
  // real layout: header banner + 4 KPIs + pipeline card + projects overview)
  // instead of the small centered <PageSkeleton>, so switching hierarchy
  // nodes shows a clean full-page loader that fills the entire dashboard
  // area — not just a tiny centered box.
  if (!scopeUser) return <ScopeViewSkeleton />;

  const isDept = kind === "department";
  const KindIcon = isDept ? LayoutGrid : (kind === "manager" ? UserCog : UserCheck);
  const kindLabel = isDept ? "Department" : (kind === "manager" ? "Content Manager" : "Team Lead");
  const publishedPct = stats.total > 0 ? Math.round((stats.by_status["Published"] / stats.total) * 100) : 0;
  const inProgress = stats.total - (stats.by_status["Published"] || 0) - (stats.by_status["To-Do"] || 0);

  return (
    <div className="flex-1 overflow-hidden flex flex-col" data-testid="scope-view">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="px-8 py-5">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${isDept ? "from-slate-700 to-slate-900" : "from-purple-500 to-purple-600"} flex items-center justify-center text-white shrink-0`}>
                <KindIcon className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wider text-purple-600">{kindLabel}</div>
                <h1 className="font-display text-3xl font-bold text-slate-900 truncate">
                  {/* Iter 45.17 — Only show `team_name` as the header when
                      this TL scope is a REAL shared team dashboard (i.e.
                      has a matching team-mate — Content TL + Publishing
                      TL sharing the same team_name under the same CM).
                      A solo TL (or a CM self-picked-as-TL whose
                      team_name is a stale label like "Komal Batman")
                      must show the person's actual name, otherwise the
                      header reads misleadingly (e.g. "Komal Batman"
                      instead of "Komal Sharma"). */}
                  {kind === "tl"
                    && scopeUser.team_name
                    && Array.isArray(scopeUser.team_mates)
                    && scopeUser.team_mates.length > 0
                      ? scopeUser.team_name
                      : scopeUser.name}
                </h1>
                {/* Emails — for a team scope, both TLs (Content + Publishing) are
                    contacts of this shared dashboard. Fall back to the single
                    scopeUser.email when there are no team-mates. */}
                {kind === "tl" && Array.isArray(scopeUser.team_mates) && (scopeUser.team_mates.length > 0 || scopeUser.email) ? (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1" data-testid="scope-team-emails">
                    {[scopeUser, ...(scopeUser.team_mates || [])]
                      .filter((u) => u && u.email)
                      .map((u) => {
                        const isPub = u.tl_kind === "publishing";
                        const kindLbl = isPub ? "Publishing TL" : "Content TL";
                        const cls = isPub ? "border-purple-200 bg-purple-50 text-purple-700" : "border-emerald-200 bg-emerald-50 text-emerald-700";
                        return (
                          <div key={u.id} className="flex items-center gap-1.5 text-sm text-slate-500" data-testid={`scope-tl-email-${u.id}`}>
                            <span className={`text-[10px] px-1.5 py-[1px] rounded border ${cls} font-medium`}>{kindLbl}</span>
                            <a href={`mailto:${u.email}`} className="hover:text-purple-600 hover:underline">{u.email}</a>
                          </div>
                        );
                      })}
                  </div>
                ) : (
                  scopeUser.email && <div className="text-sm text-slate-500 mt-0.5">{scopeUser.email}</div>
                )}
                {isDept && <div className="text-sm text-slate-500 mt-0.5">{projects.length} projects · {stats.total} tasks</div>}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <RefreshControl onRefresh={reload} storageKey="scopeAutoRefreshMs" testId="scope-refresh" />
              <div className="inline-flex bg-slate-100 rounded-lg p-1 gap-0.5">
                <button onClick={() => setView("visual")}
                  className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition ${view === "visual" ? "bg-white text-purple-700 shadow-sm" : "text-slate-600"}`}
                  data-testid="scope-view-visual">
                  <KanbanSquare className="h-3.5 w-3.5" /> Visual
                </button>
                <button onClick={() => setView("list")}
                  className={`px-3 py-1.5 rounded text-xs font-medium flex items-center gap-1.5 transition ${view === "list" ? "bg-white text-purple-700 shadow-sm" : "text-slate-600"}`}
                  data-testid="scope-view-list">
                  <LayoutList className="h-3.5 w-3.5" /> List
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="px-8 py-6 space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Total tasks" value={stats.total} icon={ClipboardList} color="indigo" />
            <StatCard label="Projects" value={projects.length} icon={FolderOpen} color="blue" />
            <StatCard label="Published" value={`${stats.by_status["Published"] || 0}`} sub={`${publishedPct}% completion`} icon={CheckCircle2} color="emerald" />
            <StatCard label="In progress" value={inProgress} icon={TrendingUp} color="amber" />
          </div>

          {/* Pipeline distribution */}
          <Card className="p-5 bg-white border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-display font-semibold text-slate-900">Pipeline distribution</h3>
              <span className="text-xs text-slate-500">{stats.total} tasks · click a status to drill down</span>
            </div>
            {stats.total === 0 ? (
              <div className="text-sm text-slate-400 py-4 text-center">No tasks yet</div>
            ) : (
              <>
                <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
                  {STATUSES.map((s) => {
                    const count = stats.by_status[s] || 0;
                    const pct = (count / stats.total) * 100;
                    if (pct === 0) return null;
                    const isActive = filters.status === s && !filters.project_id;
                    return (
                      <button
                        key={s}
                        onClick={() => showListForStatus(s)}
                        className={`${STATUS_META[s].bar} transition hover:opacity-90 ${isActive ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                        style={{ width: `${pct}%` }}
                        title={`${s}: ${count} · click to view`}
                        data-testid={`pipeline-bar-${s}`}
                      />
                    );
                  })}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
                  {STATUSES.map((s) => {
                    const M = STATUS_META[s];
                    const c = stats.by_status[s] || 0;
                    const Icon = M.icon;
                    const isActive = filters.status === s && !filters.project_id;
                    return (
                      <button
                        key={s}
                        onClick={() => showListForStatus(s)}
                        className={`p-3 rounded-lg border text-left transition group ${isActive ? `${M.active} shadow-md scale-[1.02]` : `${M.cls} hover:shadow-sm hover:brightness-95`}`}
                        data-testid={`status-card-${s}`}
                        data-active={isActive || undefined}
                      >
                        <div className="flex items-center gap-1.5">
                          <Icon className="h-3.5 w-3.5" />
                          <span className="text-[10px] font-semibold uppercase tracking-wider">{s}</span>
                          {isActive ? (
                            <Check className="h-3 w-3 ml-auto" />
                          ) : (
                            <ArrowUpRight className="h-3 w-3 ml-auto opacity-0 group-hover:opacity-100 transition" />
                          )}
                        </div>
                        <div className="font-display text-xl font-bold mt-1">{c}</div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </Card>

          {/* Projects overview — per-project mini pipeline (visual view only) */}
          {view === "visual" && (
            <Card className="p-5 bg-white border-slate-200 overflow-hidden" data-testid="projects-overview">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-semibold text-slate-900">Projects overview</h3>
                <span className="text-xs text-slate-500">{projects.length} project{projects.length === 1 ? "" : "s"}</span>
              </div>
              {projects.length === 0 ? (
                <div className="text-sm text-slate-400 py-4 text-center">No projects assigned.</div>
              ) : (
                <div className="space-y-4">
                  {projects.map((p) => {
                    const s = stats.per_project[p.id] || { total: 0 };
                    const total = s.total || 0;
                    const published = s["Published"] || 0;
                    const pct = total ? Math.round((published / total) * 100) : 0;
                    return (
                      <div key={p.id} className="rounded-lg border border-slate-200 hover:border-slate-300 transition p-4 bg-slate-50/40" data-testid={`project-overview-${p.id}`}>
                        <div className="flex items-center justify-between mb-2.5">
                          <Link to={`/projects/${p.id}`} className="font-medium text-slate-900 hover:text-purple-700 flex items-center gap-2 group min-w-0">
                            <FolderOpen className="h-4 w-4 text-slate-400 group-hover:text-purple-500 shrink-0" />
                            <span className="truncate">{p.name}</span>
                            <ArrowUpRight className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition" />
                          </Link>
                          <div className="flex items-center gap-3 text-xs text-slate-600 shrink-0">
                            <span><span className="font-medium text-slate-900">{total}</span> tasks</span>
                            <span className="text-emerald-600 font-medium">{pct}% complete</span>
                          </div>
                        </div>

                        {/* Mini pipeline bar */}
                        {total > 0 ? (
                          <div className="flex h-2.5 rounded-full overflow-hidden bg-slate-100 mb-3">
                            {STATUSES.map((st) => {
                              const c = s[st] || 0;
                              const pctStatus = total ? (c / total) * 100 : 0;
                              if (pctStatus === 0) return null;
                              const isActive = filters.status === st && filters.project_id === p.id;
                              return (
                                <button
                                  key={st}
                                  onClick={() => showListForStatus(st, p.id)}
                                  className={`${STATUS_META[st].bar} transition hover:opacity-90 ${isActive ? "ring-2 ring-offset-1 ring-slate-900" : ""}`}
                                  style={{ width: `${pctStatus}%` }}
                                  title={`${st}: ${c}`}
                                  data-testid={`pipeline-bar-${p.id}-${st}`}
                                />
                              );
                            })}
                          </div>
                        ) : (
                          <div className="h-2.5 rounded-full bg-slate-100 mb-3" />
                        )}

                        {/* Per-status count chips */}
                        <div className="grid grid-cols-5 gap-2">
                          {STATUSES.map((st) => {
                            const M = STATUS_META[st];
                            const c = s[st] || 0;
                            const Icon = M.icon;
                            const isActive = filters.status === st && filters.project_id === p.id;
                            const isEmpty = c === 0;
                            return (
                              <button
                                key={st}
                                onClick={() => !isEmpty && showListForStatus(st, p.id)}
                                disabled={isEmpty}
                                className={`p-2 rounded-md border text-left transition ${
                                  isEmpty
                                    ? "border-slate-100 bg-slate-50 text-slate-300 cursor-default"
                                    : isActive
                                    ? `${M.active} shadow-sm`
                                    : `${M.cls} hover:brightness-95 hover:shadow-sm`
                                }`}
                                data-testid={`cell-${p.id}-${st}`}
                                data-active={isActive || undefined}
                              >
                                <div className="flex items-center gap-1">
                                  <Icon className="h-3 w-3" />
                                  <span className="text-[9px] font-semibold uppercase tracking-wide leading-tight">{st}</span>
                                </div>
                                <div className="font-display text-base font-bold mt-0.5 tabular-nums">{c}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          )}

          {/* Team Members (visual view only) */}
          {view === "visual" && (
            <TeamMembersCard kind={kind} id={id} users={users} projects={projects} tasks={tasks} taskCountByUser={scopeStats?.by_user} />
          )}

          {/* Publishing Overview — embedded panel scoped to this TL / CM / Department.
              Only shown for the Content department where publishing schedules exist. */}
          {view === "visual" && (scopeDept === "content") && (
            <Card className="p-5 bg-white border-slate-200" data-testid="scope-publishing-overview">
              <PublishingOverviewPanel
                embedded
                tlId={kind === "tl" ? id : undefined}
                managerId={kind === "manager" ? id : undefined}
                department={kind === "department" ? id : undefined}
                title="Publishing Overview"
                subtitle={
                  kind === "tl" ? "This team lead's aggregated publishing calendar"
                  : kind === "manager" ? "Aggregated across all TLs you manage"
                  : "Aggregated across all TLs in this department"
                }
              />
            </Card>
          )}
        </div>

        {/* Filters + task view */}
        <div className="px-8 pb-8 space-y-4" data-testid="scope-task-list">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display font-semibold text-slate-900 mr-2">All tasks</h3>
            <div className="relative">
              <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-8 h-8 w-56 text-sm" data-testid="scope-search" />
            </div>
            <FilterSelect icon={Filter} label="Status" value={filters.status} onChange={(v) => setFilters({ ...filters, status: v })} options={STATUSES.map((s) => ({ value: s, label: s }))} testId="scope-filter-status" />
            <FilterSelect label="Project" value={filters.project_id} onChange={(v) => setFilters({ ...filters, project_id: v })} options={projects.map((p) => ({ value: p.id, label: p.name }))} testId="scope-filter-project" />
            <FilterSelect label="Writer" value={filters.writer_id} onChange={(v) => setFilters({ ...filters, writer_id: v })} options={assignables.map((u) => ({ value: u.id, label: u.name }))} testId="scope-filter-writer" />
            <FilterSelect label="Publisher" value={filters.publisher_id} onChange={(v) => setFilters({ ...filters, publisher_id: v })} options={assignables.map((u) => ({ value: u.id, label: u.name }))} testId="scope-filter-publisher" />
            {activeFilterCount > 0 && (
              <button onClick={() => setFilters({ status: "", writer_id: "", publisher_id: "", project_id: "" })} className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" data-testid="scope-clear-filters">
                <X className="h-3 w-3" /> Clear
              </button>
            )}
            <span className="text-xs text-slate-500 ml-auto">{filtered.length} of {tasks.length} tasks</span>
          </div>

          {view === "visual" ? (
            <>
              {/* Iter 42.8 — banner explaining that Board view samples the
                  300 most-recent tasks. Server-aggregated KPIs above are
                  still 100% accurate for the full scope; only the kanban is
                  bounded to keep the tab from OOM-crashing at 5000+ tasks. */}
              {scopeStats && typeof scopeStats.total === "number" && scopeStats.total > tasks.length && (
                <div
                  data-testid="board-sample-banner"
                  className="rounded-md bg-purple-50/70 border border-purple-200 text-purple-800 px-3 py-2 text-xs flex items-start gap-2"
                >
                  <Sparkles className="h-3.5 w-3.5 mt-0.5 shrink-0 text-purple-500" />
                  <span>
                    Showing the <strong>{tasks.length.toLocaleString()}</strong> most-recently-updated tasks on the board.
                    KPIs and pipeline above reflect the full <strong>{scopeStats.total.toLocaleString()}</strong> tasks in this scope —
                    <button
                      onClick={() => setView("list")}
                      className="ml-1 underline underline-offset-2 hover:text-purple-900"
                      data-testid="banner-switch-to-list"
                    >switch to List view</button> or narrow filters to see the rest.
                  </span>
                </div>
              )}
              <div className="rounded-xl overflow-hidden border border-slate-200 bg-white">
                <div className="h-[600px] flex">
                  <BoardView tasks={filtered} users={users} projects={projects} showProject
                    onStatusChange={(taskId, status) => updateTask(taskId, { status })} />
                </div>
              </div>
            </>
          ) : (
            <Card className="bg-white border-slate-200 overflow-hidden">
              <div ref={scrollContainerRef} className="overflow-auto scrollbar-thin smooth-scroll max-h-[700px]">
                <table className="spreadsheet-table" data-testid="scope-tasks-table">
                  <thead>
                    <tr>
                      {canBulk && (
                        <th className="w-10">
                          <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="scope-select-all"
                            data-state={allSelected ? "checked" : someSelected ? "indeterminate" : "unchecked"} />
                        </th>
                      )}
                      {canBulk && <th className="w-8"></th>}
                      {renderScopeHeader(0, "w-24")}
                      {renderScopeHeader(1, "min-w-[220px]")}
                      {renderScopeHeader(2, "w-40")}
                      {renderScopeHeader(3, "w-32")}
                      {renderScopeHeader(4, "w-32")}
                      {renderScopeHeader(5, "w-36")}
                      {renderScopeHeader(6, "w-36")}
                      {renderScopeHeader(7, "w-36")}
                      {renderScopeHeader(8, "w-24")}
                      {extraVisibleColumnDefs.map((col) => {
                        const cIdx = scopeColumnDefs.findIndex((c) => c.key === col.key);
                        return (
                          <th key={col.key} className={col.isCustom ? "min-w-[140px]" : "w-32"} data-testid={`scope-th-${col.key}`}>
                            <div className="flex items-center gap-1">
                              <span className="flex-1 truncate">{col.label}</span>
                              {canBulk && col.editable && cIdx >= 0 && (
                                <HeaderMenu
                                  col={col} isAdmin={false} canBulk={canBulk}
                                  onBulkUpdate={() => setBulkUpdateCol(col)}
                                  onClearColumn={() => onScopeColClear(col)}
                                />
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr><td colSpan={9 + extraVisibleColumnDefs.length + (canBulk ? 2 : 0)} className="text-center text-slate-400 py-10">No tasks match.</td></tr>
                    )}
                    {/* Iter 45.3 — virtualized rows (see comment near
                        useVirtualizer above). Only ~30 rows in the DOM at
                        once. Top/bottom spacer <tr>s reserve the full
                        scroll height so the scrollbar reflects the total
                        row count. `virtualRow.index` matches the position
                        sheet-kit uses, keeping keyboard nav, autofill,
                        selection and drag-select fully functional at any
                        scale. */}
                    {filtered.length > 0 && virtualPaddingTop > 0 && (
                      <tr aria-hidden="true" style={{ height: virtualPaddingTop }} />
                    )}
                    {filtered.length > 0 && virtualItems.map((virtualRow) => {
                      const idx = virtualRow.index;
                      const t = filtered[idx];
                      if (!t) return null;
                      const isSelected = selected.has(t.id);
                      const cpH = (c) => {
                        const cp = sheet.cellProps(idx, c);
                        return { ...cp, style: { position: "relative" } };
                      };
                      const AutofillHandle = ({ c }) => {
                        if (!sheet.isBottomRightOfRange(idx, c)) return null;
                        const col = scopeColumnDefs[c];
                        if (!col || !col.editable) return null;
                        return (
                          <div className="autofill-handle" data-testid={`scope-autofill-${idx}-${c}`}
                            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); sheet.beginAutofill(); }} />
                        );
                      };
                      const isEditingKw = editingCoord?.taskId === t.id && editingCoord?.field === "keyword";
                      const isEditingNiche = editingCoord?.taskId === t.id && editingCoord?.field === "niche";
                      return (
                      <ContextMenu key={t.id}>
                        <ContextMenuTrigger asChild>
                          <tr data-testid={`scope-task-${t.id}`} className={isSelected ? "bg-purple-50/50" : ""}>
                            {canBulk && (
                              <td className="text-center">
                                <Checkbox checked={isSelected} onCheckedChange={() => toggleOne(t.id)} data-testid={`scope-select-task-${t.id}`} />
                              </td>
                            )}
                            {canBulk && (
                              <td onMouseDown={(e) => startRowSelect(idx, e)} onMouseEnter={() => dragOverRow(idx)} onMouseOver={() => dragOverRow(idx)}
                                className={`text-center text-xs font-mono select-none cursor-crosshair ${isSelected ? "bg-purple-100 text-purple-700 font-semibold" : "text-slate-400"}`}
                                data-testid={`scope-row-num-${t.id}`}>
                                {idx + 1}
                              </td>
                            )}
                            <td {...cpH(0)} className={`${cpH(0).className} text-xs text-slate-600`} data-testid={`scope-cell-date-${t.id}`}>
                              {t.date}
                              <AutofillHandle c={0} />
                            </td>
                            <td {...cpH(1)} className={`${cpH(1).className}`} data-testid={`scope-cell-keyword-${t.id}`}>
                              {isEditingKw ? (
                                <ScopeInlineText initial={t.keyword || ""} onCommit={(v) => { updateTask(t.id, { keyword: v }); setEditingCoord(null); sheet.setEditing(null); }} onCancel={() => { setEditingCoord(null); sheet.setEditing(null); }} testId={`scope-edit-keyword-${t.id}`} />
                              ) : (
                                <span onDoubleClick={() => { sheet.setEditing({ r: idx, c: 1 }); }} className="text-slate-900 cursor-text">
                                  {t.keyword || <span className="text-slate-300 italic">(empty)</span>}
                                </span>
                              )}
                              <AutofillHandle c={1} />
                            </td>
                            <td {...cpH(2)} className={`${cpH(2).className}`} data-testid={`scope-cell-project-${t.id}`}>
                              <Link to={`/projects/${t.project_id}`} className="text-xs text-purple-600 hover:underline">
                                {projectMap[t.project_id]?.name || "—"}
                              </Link>
                            </td>
                            <td {...cpH(3)} className={`${cpH(3).className} text-xs`} data-testid={`scope-cell-niche-${t.id}`}>
                              {isEditingNiche ? (
                                <ScopeInlineText initial={t.niche || ""} onCommit={(v) => { updateTask(t.id, { niche: v }); setEditingCoord(null); sheet.setEditing(null); }} onCancel={() => { setEditingCoord(null); sheet.setEditing(null); }} testId={`scope-edit-niche-${t.id}`} />
                              ) : (
                                <span onDoubleClick={() => { sheet.setEditing({ r: idx, c: 3 }); }} className="cursor-text">
                                  {t.niche || <span className="text-slate-300">—</span>}
                                </span>
                              )}
                              <AutofillHandle c={3} />
                            </td>
                            <td {...cpH(4)} className={cpH(4).className} data-testid={`scope-cell-method-${t.id}`}>
                              <ScopeMethodSelect value={t.method} onChange={(v) => updateTask(t.id, { method: v })} testId={`scope-method-${t.id}`} />
                              <AutofillHandle c={4} />
                            </td>
                            <td {...cpH(5)} className={cpH(5).className} data-testid={`scope-cell-writer-${t.id}`}>
                              <ScopeUserSelect users={assignables} value={t.writer_id}
                                onChange={(v) => updateTask(t.id, { writer_id: v })} testId={`scope-writer-${t.id}`} />
                              <AutofillHandle c={5} />
                            </td>
                            <td {...cpH(6)} className={cpH(6).className} data-testid={`scope-cell-publisher-${t.id}`}>
                              <ScopeUserSelect users={assignables} value={t.publisher_id}
                                onChange={(v) => updateTask(t.id, { publisher_id: v })} testId={`scope-publisher-${t.id}`} />
                              <AutofillHandle c={6} />
                            </td>
                            <td {...cpH(7)} className={cpH(7).className} data-testid={`scope-cell-status-${t.id}`}>
                              <ScopeStatusSelect value={t.status} onChange={(v) => updateTask(t.id, { status: v })} testId={`scope-status-${t.id}`} />
                              <AutofillHandle c={7} />
                            </td>
                            <td {...cpH(8)} className={cpH(8).className} data-testid={`scope-cell-docs-${t.id}`}>
                              {t.docs && (
                                <a href={t.docs} target="_blank" rel="noreferrer" className="text-xs text-purple-600 hover:underline flex items-center gap-1">
                                  <ExternalLink className="h-3 w-3" /> Open
                                </a>
                              )}
                              <AutofillHandle c={8} />
                            </td>
                            {extraVisibleColumnDefs.map((col) => {
                              const cIdx = scopeColumnDefs.findIndex((c) => c.key === col.key);
                              const tdProps = cIdx >= 0 ? cpH(cIdx) : {};
                              const handleNode = cIdx >= 0 ? <AutofillHandle c={cIdx} /> : null;
                              const rawCol = col.isCustom
                                ? (deptColumns.find((c) => c.id === col.colId) || col)
                                : { id: col.key, type: col.type, options: col.options, key: col.key };
                              const val = col.isCustom ? (t.custom?.[col.colId] ?? "") : col.getVal(t);
                              const onSave = col.isCustom
                                ? (v) => updateTask(t.id, { custom: { ...(t.custom || {}), [col.colId]: v } })
                                : (v) => updateTask(t.id, col.setValInto(v, t));
                              return (
                                <CustomCell
                                  key={col.key}
                                  col={rawCol}
                                  value={val}
                                  onSave={onSave}
                                  taskId={t.id}
                                  members={col.type === "member" ? assignables : undefined}
                                  tdProps={tdProps}
                                  handle={handleNode}
                                />
                              );
                            })}
                          </tr>
                        </ContextMenuTrigger>
                        <ContextMenuContent className="w-52" data-testid={`scope-ctx-${t.id}`}>
                          <ContextMenuItem onSelect={() => sheet.copySelection()} data-testid={`scope-ctx-copy-${t.id}`}>
                            <Copy className="h-3.5 w-3.5 mr-2" /> Copy
                          </ContextMenuItem>
                          <ContextMenuItem onSelect={async () => {
                            try { const txt = await navigator.clipboard.readText(); if (txt) await sheet.pasteAtFocus(txt); }
                            catch { toast.error("Clipboard read blocked — use Ctrl+V"); }
                          }} data-testid={`scope-ctx-paste-${t.id}`}>
                            <ClipboardPaste className="h-3.5 w-3.5 mr-2" /> Paste
                          </ContextMenuItem>
                          <ContextMenuItem onSelect={() => sheet.clearRange()} data-testid={`scope-ctx-clear-${t.id}`}>
                            <X className="h-3.5 w-3.5 mr-2" /> Clear cells
                          </ContextMenuItem>
                          <ContextMenuSeparator />
                          <ContextMenuItem onSelect={async () => {
                            const rangeIds = sheet.rangeTaskIds();
                            const ids = rangeIds.length > 1 ? rangeIds : [t.id];
                            if (!confirm(`Delete ${ids.length} task${ids.length === 1 ? "" : "s"}?`)) return;
                            const snapshots = tasks.filter((x) => ids.includes(x.id));
                            let liveIds = [...ids];
                            try {
                              await api.post("/tasks/bulk-delete", { task_ids: liveIds });
                              setTasks((prev) => prev.filter((x) => !liveIds.includes(x.id)));
                              sheet.pushUndo({
                                label: `delete ${snapshots.length} task${snapshots.length === 1 ? "" : "s"}`,
                                revert: async () => {
                                  const newIds = [];
                                  for (const s of snapshots) {
                                    const { id: _oid, created_at, updated_at, order, ...rest } = s;
                                    try {
                                      const { data: created } = await api.post("/tasks", { ...rest, project_id: s.project_id, position: "bottom" });
                                      if (created && created.id) newIds.push(created.id);
                                    } catch (e) { console.debug("scope-view catch:", e&&e.message); }
                                  }
                                  if (newIds.length) liveIds = newIds;
                                  reload();
                                },
                                redo: async () => {
                                  try { await api.post("/tasks/bulk-delete", { task_ids: liveIds }); } catch (e) { console.debug("scope-view catch:", e&&e.message); }
                                  reload();
                                },
                              });
                              toast.success(`Deleted ${liveIds.length} task${liveIds.length === 1 ? "" : "s"}`);
                            } catch (e) { toast.error(formatApiError(e)); }
                          }} className="text-red-600 focus:text-red-700 focus:bg-red-50" data-testid={`scope-ctx-delete-${t.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-2" />
                            {sheet.rangeTaskIds().length > 1 ? `Delete ${sheet.rangeTaskIds().length} rows` : "Delete row"}
                          </ContextMenuItem>
                        </ContextMenuContent>
                      </ContextMenu>
                      );
                    })}
                    {filtered.length > 0 && virtualPaddingBottom > 0 && (
                      <tr aria-hidden="true" style={{ height: virtualPaddingBottom }} />
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Floating bulk action bar */}
      {canBulk && selected.size > 0 && view === "list" && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-slate-900 text-white rounded-xl shadow-2xl border border-slate-800 flex items-center gap-2 px-4 py-2.5 max-w-[calc(100vw-2rem)] overflow-x-auto scrollbar-thin"
             data-testid="scope-bulk-action-bar">
          <div className="flex items-center gap-2 pr-3 border-r border-slate-700 shrink-0">
            <div className="h-6 w-6 rounded-full bg-purple-500 flex items-center justify-center text-xs font-bold">{selected.size}</div>
            <span className="text-sm font-medium whitespace-nowrap">selected</span>
          </div>
          <Select onValueChange={(v) => bulkPatch({ status: v })}>
            <SelectTrigger className="h-8 text-xs w-40 bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="scope-bulk-set-status">
              <SelectValue placeholder="Set status…" />
            </SelectTrigger>
            <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkPatch({ method: v })}>
            <SelectTrigger className="h-8 text-xs w-32 bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="scope-bulk-set-method">
              <SelectValue placeholder="Set method…" />
            </SelectTrigger>
            <SelectContent>{METHOD_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkPatch({ writer_id: v })}>
            <SelectTrigger className="h-8 text-xs w-40 bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="scope-bulk-set-writer">
              <SelectValue placeholder="Assign writer…" />
            </SelectTrigger>
            <SelectContent>{assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={(v) => bulkPatch({ publisher_id: v })}>
            <SelectTrigger className="h-8 text-xs w-40 bg-slate-800 border-slate-700 text-white hover:bg-slate-700" data-testid="scope-bulk-set-publisher">
              <SelectValue placeholder="Assign publisher…" />
            </SelectTrigger>
            <SelectContent>{assignables.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="destructive" className="h-8" onClick={bulkDelete} data-testid="scope-bulk-delete-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
          </Button>
          <button onClick={() => setSelected(new Set())} className="p-1.5 rounded hover:bg-slate-700 text-slate-400 hover:text-white" data-testid="scope-clear-selection" title="Clear selection">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      <BulkUpdateColumnDialog
        open={!!bulkUpdateCol}
        onOpenChange={(o) => !o && setBulkUpdateCol(null)}
        col={bulkUpdateCol}
        filteredCount={filtered.length}
        onApply={(val, limit) => applyBulkUpdate(bulkUpdateCol, val, limit)}
      />
    </div>
  );
}

function ScopeInlineText({ initial, onCommit, onCancel, testId }) {
  const [v, setV] = useState(initial ?? "");
  return (
    <input autoFocus type="text" value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(v)}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") onCancel(); }}
      className="w-full text-xs px-1 py-0.5 border border-purple-500 rounded bg-white"
      data-testid={testId} />
  );
}

function ScopeUserSelect({ users, value, onChange, testId }) {
  return (
    <Select value={value || "unassigned"} onValueChange={(v) => onChange(v === "unassigned" ? null : v)}>
      <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent" data-testid={testId}>
        <SelectValue placeholder="Unassigned" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="unassigned">Unassigned</SelectItem>
        {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function ScopeStatusSelect({ value, onChange, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className={`h-7 text-xs font-medium border ${STATUS_META[value]?.cls || ""}`} data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function ScopeMethodSelect({ value, onChange, testId }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="h-7 text-xs border-transparent hover:border-slate-200 bg-transparent" data-testid={testId}>
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">—</SelectItem>
        {METHOD_OPTIONS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}



function TeamMembersCard({ kind, id, users, projects, tasks, taskCountByUser }) {
  // Iter 42.6 — prefer server-aggregated per-user counts (`taskCountByUser`
  // from /dashboard/scope-stats.by_user). Falls back to the client-side
  // iteration when the endpoint payload isn't in yet so the card still
  // renders correctly on first paint or after an error.
  const projectIds = new Set(projects.map((p) => p.id));
  let taskCountByUserComputed = taskCountByUser;
  if (!taskCountByUserComputed) {
    taskCountByUserComputed = {};
    tasks.forEach((t) => {
      if (t.writer_id) taskCountByUserComputed[t.writer_id] = (taskCountByUserComputed[t.writer_id] || 0) + 1;
      if (t.publisher_id) taskCountByUserComputed[t.publisher_id] = (taskCountByUserComputed[t.publisher_id] || 0) + 1;
    });
  }

  // Which team members belong to this scope
  let members = [];
  if (kind === "manager") {
    // Manager + their TLs + their associates
    const cm = users.find((u) => u.id === id);
    const tls = users.filter((u) => u.role === "team_lead" && u.manager_id === id);
    const tlIds = new Set(tls.map((t) => t.id));
    const associates = users.filter((u) => u.role === "associate" && tlIds.has(u.team_lead_id));
    members = [cm, ...tls, ...associates].filter(Boolean);
  } else if (kind === "tl") {
    // TL + their associates
    const tl = users.find((u) => u.id === id);
    const associates = users.filter((u) => u.role === "associate" && u.team_lead_id === id);
    members = [tl, ...associates].filter(Boolean);
  } else {
    // Department: all users in that department (except admins) + admin
    members = users.filter((u) => ((u.role === "admin" || u.role === "super_admin")) || (u.department || "content") === id);
  }

  if (members.length === 0) return null;

  const byRole = {
    content_manager: members.filter((m) => m.role === "content_manager"),
    team_lead: members.filter((m) => m.role === "team_lead"),
    associate: members.filter((m) => m.role === "associate"),
    admin: members.filter((m) => (m.role === "admin" || m.role === "super_admin")),
  };

  const totalMembers = members.length;

  return (
    <Card className="p-5 bg-white border-slate-200 overflow-hidden" data-testid="team-members-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2">
            <Users className="h-4 w-4 text-purple-500" /> Team members
          </h3>
          <div className="text-xs text-slate-500 mt-0.5">
            {totalMembers} {totalMembers === 1 ? "person" : "people"} in this scope
          </div>
        </div>
        <div className="flex items-center gap-2">
          {["content_manager", "team_lead", "associate"].map((r) => {
            const c = byRole[r].length;
            if (c === 0) return null;
            const M = ROLE_META[r];
            return (
              <div key={r} className={`px-2 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider border ${M.cls}`}>
                {c} {M.label}{c > 1 ? "s" : ""}
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {members.map((m) => {
          const M = ROLE_META[m.role] || ROLE_META.associate;
          const initials = (m.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
          const tasksCount = taskCountByUserComputed[m.id] || 0;
          const avatarColor = m.role === "content_manager"
            ? "bg-gradient-to-br from-purple-500 to-purple-600 text-white"
            : m.role === "team_lead"
            ? "bg-gradient-to-br from-blue-500 to-cyan-600 text-white"
            : (m.role === "admin" || m.role === "super_admin")
            ? "bg-gradient-to-br from-purple-600 to-pink-600 text-white"
            : "bg-slate-100 text-slate-700";
          return (
            <div
              key={m.id}
              className="group flex items-center gap-3 p-3 rounded-lg border border-slate-200 hover:border-purple-300 hover:shadow-sm transition bg-white"
              data-testid={`member-${m.id}`}
            >
              <div className={`h-10 w-10 rounded-full flex items-center justify-center font-semibold text-sm ${avatarColor} shrink-0`}>
                {initials}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 truncate text-sm">{m.name}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9px] font-semibold uppercase tracking-wider border ${M.cls}`}>
                    {M.label}
                  </span>
                  {(m.role === "associate" || m.role === "team_lead") && tasksCount > 0 && (
                    <span className="text-[10px] text-slate-500">
                      · {tasksCount} task{tasksCount === 1 ? "" : "s"}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}


function StatCard({ label, value, sub, icon: Icon, color }) {  const colors = {
    indigo: "bg-purple-50 text-purple-600 border-purple-100",
    blue: "bg-blue-50 text-blue-600 border-blue-100",
    emerald: "bg-emerald-50 text-emerald-600 border-emerald-100",
    amber: "bg-amber-50 text-amber-600 border-amber-100",
  };
  return (
    <Card className="p-5 bg-white border-slate-200">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{value}</div>
          {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
        </div>
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center border ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </Card>
  );
}

function FilterSelect({ label, value, onChange, options, icon: Icon, testId }) {
  return (
    <Select value={value || "any"} onValueChange={(v) => onChange(v === "any" ? "" : v)}>
      <SelectTrigger className="h-8 text-xs w-auto min-w-[130px]" data-testid={testId}>
        {Icon && <Icon className="h-3 w-3 mr-1.5" />}
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="any">All {label}s</SelectItem>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
